import React, { useState } from 'react';
import {
  Row,
  Col,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Divider,
  Space,
  Alert,
  Spin,
  Empty,
  Typography,
  Select,
  Switch,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usePlans } from '../../hooks/usePlans';
import { PlanCard } from './PlanCard';
import type {
  Plan,
  CreatePlanDto,
  UpdatePlanDto,
} from '../../services/plan.service';

const { Title } = Typography;

interface PlanFormValues {
  name: string;
  display_name: string;
  slug: string;
  tier: number;
  pricing: { monthly: number; yearly: number };
  limits: {
    max_stores: number | null;
    max_users: number | null;
    max_products: number | null;
  };
  features: string[];
  is_active: boolean;
  sort_order: number;
}

const DEFAULT_FORM_VALUES: PlanFormValues = {
  name: '',
  display_name: '',
  slug: '',
  tier: 1,
  pricing: { monthly: 0, yearly: 0 },
  limits: { max_stores: null, max_users: null, max_products: null },
  features: [],
  is_active: true,
  sort_order: 0,
};

const planToFormValues = (plan: Plan): PlanFormValues => ({
  name: plan.name,
  display_name: plan.display_name,
  slug: plan.slug,
  tier: plan.tier,
  pricing: { monthly: plan.pricing.monthly, yearly: plan.pricing.yearly },
  limits: {
    max_stores: plan.limits.max_stores,
    max_users: plan.limits.max_users,
    max_products: plan.limits.max_products,
  },
  features: plan.features,
  is_active: plan.is_active,
  sort_order: plan.sort_order,
});

export const PlanList: React.FC = () => {
  const navigate = useNavigate();
  const {
    plans,
    loading,
    error,
    refetch,
    createPlan,
    updatePlan,
    activatePlan,
    deactivatePlan,
    deletePlan,
  } = usePlans();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [form] = Form.useForm<PlanFormValues>();

  const handleCreate = () => {
    setEditingPlan(null);
    form.resetFields();
    form.setFieldsValue(DEFAULT_FORM_VALUES);
    setIsModalVisible(true);
  };

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);
    form.resetFields();
    form.setFieldsValue(planToFormValues(plan));
    setIsModalVisible(true);
  };

  const handleViewTenants = (plan: Plan) => {
    navigate(`/plans/${plan._id}/tenants`);
  };

  const handleDelete = (plan: Plan) => {
    Modal.confirm({
      title: `Xóa gói: ${plan.display_name}?`,
      content:
        'Hành động này không thể hoàn tác. Chỉ xóa được gói chưa có tenant nào sử dụng.',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        await deletePlan(plan._id);
      },
    });
  };

  const handleToggleStatus = (plan: Plan) => {
    if (plan.is_active) {
      Modal.confirm({
        title: `Vô hiệu hóa gói: ${plan.display_name}?`,
        content:
          'Tenant mới sẽ không thể đăng ký gói này, nhưng tenant hiện tại vẫn dùng được.',
        okText: 'Vô hiệu hóa',
        cancelText: 'Hủy',
        onOk: async () => {
          await deactivatePlan(plan._id);
        },
      });
    } else {
      activatePlan(plan._id);
    }
  };

  const handleModalOk = async () => {
    const values = await form.validateFields();
    if (editingPlan) {
      const dto: UpdatePlanDto = {
        display_name: values.display_name,
        pricing: values.pricing,
        limits: values.limits,
        features: values.features,
        is_active: values.is_active,
        sort_order: values.sort_order,
      };
      await updatePlan(editingPlan._id, dto);
    } else {
      const dto: CreatePlanDto = {
        name: values.name,
        display_name: values.display_name,
        slug: values.slug,
        tier: values.tier,
        pricing: values.pricing,
        limits: values.limits,
        features: values.features,
        is_active: values.is_active,
        sort_order: values.sort_order,
      };
      await createPlan(dto);
    }
    setIsModalVisible(false);
    form.resetFields();
  };

  const plansSorted = [...plans].sort(
    (a, b) => a.sort_order - b.sort_order || a.tier - b.tier,
  );

  const affectedCount = editingPlan?.tenant_count ?? 0;

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Gói cước
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="large"
            >
              Tạo gói mới
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={refetch}
              loading={loading}
            />
          </Space>
        </Col>
      </Row>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Tải gói cước thất bại"
          description={error.message}
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" onClick={refetch}>
              Thử lại
            </Button>
          }
        />
      )}

      <Spin spinning={loading} tip="Đang tải...">
        {plansSorted.length === 0 && !loading ? (
          <Row justify="center" style={{ marginTop: 60 }}>
            <Col>
              <Empty description="Chưa có gói cước nào" />
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="primary" onClick={handleCreate}>
                  Tạo gói đầu tiên
                </Button>
              </div>
            </Col>
          </Row>
        ) : (
          <Row gutter={[24, 24]}>
            {plansSorted.map((plan) => (
              <Col key={plan._id} xs={24} sm={24} md={12} lg={6}>
                <PlanCard
                  plan={plan}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                  onViewTenants={handleViewTenants}
                />
              </Col>
            ))}
          </Row>
        )}
      </Spin>

      <Modal
        title={
          editingPlan
            ? `Sửa gói: ${editingPlan.display_name}`
            : 'Tạo gói cước mới'
        }
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={720}
        okText={editingPlan ? 'Cập nhật' : 'Tạo'}
        cancelText="Hủy"
      >
        {editingPlan && affectedCount > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`Thay đổi sẽ ảnh hưởng ${affectedCount} tenant đang dùng gói này`}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form<PlanFormValues> form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Mã (name)"
                name="name"
                rules={[{ required: true, message: 'Bắt buộc' }]}
              >
                <Input
                  placeholder="Lite"
                  disabled={!!editingPlan}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Slug"
                name="slug"
                rules={[
                  { required: true, message: 'Bắt buộc' },
                  {
                    pattern: /^[a-z0-9_-]+$/,
                    message: 'Chỉ dùng a-z, 0-9, _, -',
                  },
                ]}
              >
                <Input placeholder="lite" disabled={!!editingPlan} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Tier"
                name="tier"
                rules={[{ required: true }]}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  disabled={!!editingPlan}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Tên hiển thị"
            name="display_name"
            rules={[{ required: true, message: 'Bắt buộc' }]}
          >
            <Input placeholder="Gói Lite" />
          </Form.Item>

          <Divider>Giá</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Giá tháng (VND)"
                name={['pricing', 'monthly']}
                rules={[{ required: true, message: 'Bắt buộc' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} step={1000} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Giá năm (VND)"
                name={['pricing', 'yearly']}
                rules={[{ required: true, message: 'Bắt buộc' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} step={10000} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Giới hạn (để trống = không giới hạn)</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Cửa hàng" name={['limits', 'max_stores']}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Người dùng" name={['limits', 'max_users']}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Sản phẩm" name={['limits', 'max_products']}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Tính năng</Divider>
          <Form.Item
            label="Danh sách feature"
            name="features"
            rules={[{ required: true, message: 'Thêm ít nhất 1 tính năng' }]}
          >
            <Select
              mode="tags"
              placeholder="Nhập tên feature, gõ Enter để thêm"
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Đang kích hoạt"
                name="is_active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Thứ tự sắp xếp"
                name="sort_order"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default PlanList;
